import { type OwnershipGate, type ReconciliationEvidence, type ReconciliationTerminalPolicy, type ResumeOutcome } from "@uploads";
import { getFileUploadOwnerIdLength, isValidFileUploadOwnerId, readFileUploadSession, type FileUploadSession } from "@/db/fileUploadDb";
import { createAdminSessionStore } from "./adminSessionStore";
import { commitAdminUploadWriterFence } from "./adminUploadLease";
import { createPersistedAdminTerminalRecoveryPolicy } from "./adminTerminalRecoveryPolicy";

type AdminOwnershipStore = Pick<ReturnType<typeof createAdminSessionStore>, "findByUploadId">;
type ExactClaimIdentity = Pick<FileUploadSession, "uploadId"> & { uploadOwnerId: string; uploadOwnerClaimId: string };

/** Returned by the fenced ownerless effect when the row it was computed against is gone or claimed. */
const OWNERLESS_ROW_CHANGED = Symbol("ownerless-row-changed");

type ExactClaimTerminalEffect<T> =
	| { kind: "effect-applied"; value: T }
	| {
			kind: "claim-identity-changed";
			observedUploadId?: string;
			observedOwnerId?: unknown;
			observedOwnerClaimId?: unknown;
	  };

/**
 * One-purpose handoff for a terminal write whose resume scope has already started releasing.
 *
 * The owner id is not sufficient authority: duplicated tabs share it, and a later claim from the
 * same tab does too. Only the exact owner + claim pair stamped onto the errored in-memory session
 * may authorize this delayed write. A fresh durable writer epoch serializes the decision with any
 * successor, then the caller's effect must repeat the same identity as its own row-level CAS.
 */
export async function commitExactClaimTerminalWrite<T>(
	session: FileUploadSession,
	effect: (expectedIdentity: ExactClaimIdentity) => Promise<T>
): Promise<{ kind: "applied"; value: T } | { kind: "skipped-not-owned" }> {
	// Capture scalars before the first await. The shared driver passes the same mutable session object
	// that claim() stamped, and no later mutation may redefine which released claim is expected here.
	const expectedUploadId = session.uploadId;
	const expectedOwnerId = session.uploadOwnerId;
	const expectedOwnerClaimId = session.uploadOwnerClaimId;
	const refuse = (
		condition: "invalid-expected-claim-identity" | "durable-writer-unavailable-or-held" | "claim-identity-changed",
		observed?: Extract<ExactClaimTerminalEffect<never>, { kind: "claim-identity-changed" }>
	): { kind: "skipped-not-owned" } => {
		try {
			console.warn("[Upload] exact-claim terminal write refused", {
				condition,
				sessionId: session.id,
				uploadId: expectedUploadId,
				...(isValidFileUploadOwnerId(expectedOwnerId)
					? { expectedOwnerId }
					: { expectedOwnerIdLength: getFileUploadOwnerIdLength(expectedOwnerId) }),
				...(isValidFileUploadOwnerId(expectedOwnerClaimId)
					? { expectedOwnerClaimId }
					: { expectedOwnerClaimIdLength: getFileUploadOwnerIdLength(expectedOwnerClaimId) }),
				...(observed
					? {
							observedUploadId: observed.observedUploadId?.slice(0, 200),
							...(isValidFileUploadOwnerId(observed.observedOwnerId)
								? { observedOwnerId: observed.observedOwnerId }
								: { observedOwnerIdLength: getFileUploadOwnerIdLength(observed.observedOwnerId) }),
							...(isValidFileUploadOwnerId(observed.observedOwnerClaimId)
								? { observedOwnerClaimId: observed.observedOwnerClaimId }
								: { observedOwnerClaimIdLength: getFileUploadOwnerIdLength(observed.observedOwnerClaimId) })
						}
					: {})
			});
		} catch {
			// Diagnostics must not turn a refused terminal write into a rejected operation.
		}
		return { kind: "skipped-not-owned" };
	};
	if (!isValidFileUploadOwnerId(expectedOwnerId) || !isValidFileUploadOwnerId(expectedOwnerClaimId)) {
		return refuse("invalid-expected-claim-identity");
	}
	const expectedIdentity: ExactClaimIdentity = {
		uploadId: expectedUploadId,
		uploadOwnerId: expectedOwnerId,
		uploadOwnerClaimId: expectedOwnerClaimId
	};

	const commitIfExactClaim = async (): Promise<ExactClaimTerminalEffect<T>> => {
		const current = await readFileUploadSession(session.id);
		if (
			!current ||
			current.uploadId !== expectedIdentity.uploadId ||
			current.uploadOwnerId !== expectedIdentity.uploadOwnerId ||
			current.uploadOwnerClaimId !== expectedIdentity.uploadOwnerClaimId
		) {
			return {
				kind: "claim-identity-changed",
				observedUploadId: current?.uploadId,
				observedOwnerId: current?.uploadOwnerId,
				observedOwnerClaimId: current?.uploadOwnerClaimId
			};
		}
		return { kind: "effect-applied", value: await effect(expectedIdentity) };
	};
	const fenced = await commitAdminUploadWriterFence(
		session.id,
		commitIfExactClaim,
		// TRANSITIONAL — the test-only legacy seam has no durable coordinator. Keep the exact
		// row check and require the effect's own transactional CAS there as well.
		commitIfExactClaim,
		"admin-exact-claim-terminal"
	);
	if (fenced.kind === "skipped-not-owned") return refuse("durable-writer-unavailable-or-held");
	if (fenced.value.kind === "claim-identity-changed") return refuse("claim-identity-changed", fenced.value);
	return { kind: "applied", value: fenced.value.value };
}

export function createAdminOwnedOrUnownedGate(
	lease: OwnershipGate<FileUploadSession>,
	store: AdminOwnershipStore,
	signal?: AbortSignal
): OwnershipGate<FileUploadSession> {
	const isOwnerlessPersistedSession = async (session: FileUploadSession): Promise<boolean> => {
		const persisted = await store.findByUploadId(session.uploadId);
		return (
			persisted !== undefined &&
			persisted.uploadOwnerId === session.uploadOwnerId &&
			persisted.uploadOwnerClaimId === session.uploadOwnerClaimId &&
			persisted.uploadOwnerId === undefined
		);
	};
	/**
	 * An ownerless (handed-off) row is the one case `owns()` accepts that the upload lease can never
	 * authorize: this scope holds no durable writer token for a session it never claimed, and the
	 * lease's fenced commit re-asserts `uploadOwnerId === ownerId`, which no ownerless row can
	 * satisfy. Delegating it to the lease therefore turned every reconciliation write on a pending
	 * row into a silent no-op that consumers read as "ownership lost" (break-it round 1). The row
	 * still needs A fence, so this path takes its own durable writer epoch for the session and
	 * re-reads the row inside it: the write applies only while the row is still the same ownerless
	 * row the decision was computed against, so a successor claim landing in the window is refused
	 * instead of clobbered.
	 */
	const commitOwnerlessThroughWriterFence = async <T>(
		session: FileUploadSession,
		effect: () => Promise<T>
	): Promise<{ kind: "applied"; value: T } | { kind: "skipped-not-owned" }> => {
		const refuse = (condition: string): { kind: "skipped-not-owned" } => {
			try {
				console.warn("[Upload] ownerless reconciliation write refused", {
					condition,
					sessionId: session.id,
					uploadId: session.uploadId
				});
			} catch {
				// Diagnostics must not turn a refused write into a rejected operation.
			}
			return { kind: "skipped-not-owned" };
		};
		const fenced = await commitAdminUploadWriterFence<T | typeof OWNERLESS_ROW_CHANGED>(
			session.id,
			async () => {
				// Read the row directly rather than through the store: this runs inside the durable
				// writer's Dexie transaction, exactly like the upload lease's own fenced commit.
				const current = await readFileUploadSession(session.id);
				if (
					!current ||
					current.uploadId !== session.uploadId ||
					current.uploadOwnerId !== undefined ||
					current.uploadOwnerClaimId !== undefined
				) {
					return OWNERLESS_ROW_CHANGED;
				}
				return effect();
			},
			// TRANSITIONAL — taken only when getDurableOwnership answers `undefined`, which today only
			// the MODE sniff produces (see adminUploadLease.getDurableOwnership). The legacy row-CAS
			// harness runs there, and each reconciliation effect still performs its own
			// uploadId/owner/claim CAS — the same weaker guard the lease's own commitIfOwned keeps for
			// that harness.
			effect,
			"admin-ownerless-reconcile"
		);
		if (fenced.kind === "skipped-not-owned") return refuse("durable-writer-unavailable-or-held");
		if (fenced.value === OWNERLESS_ROW_CHANGED) return refuse("ownerless-row-claimed-or-removed");
		return { kind: "applied", value: fenced.value as T };
	};
	return {
		async owns(session) {
			if (signal?.aborted) return false;
			if (session.uploadOwnerId !== undefined && !isValidFileUploadOwnerId(session.uploadOwnerId)) {
				try {
					console.warn("[Upload] persisted-session reconciliation refused invalid owner identity", {
						sessionId: session.id,
						uploadId: session.uploadId,
						ownerIdLength: getFileUploadOwnerIdLength(session.uploadOwnerId)
					});
				} catch {
					// Diagnostics must not change the ownership refusal.
				}
				return false;
			}
			if (await lease.owns(session)) return true;
			return isOwnerlessPersistedSession(session);
		},
		async commitIfOwned<T>(
			session: FileUploadSession,
			effect: () => Promise<T>
		): Promise<{ kind: "applied"; value: T } | { kind: "skipped-not-owned" }> {
			if (signal?.aborted) return { kind: "skipped-not-owned" };
			// The shared contract keeps commitIfOwned optional for adapters that only
			// expose an owns() observation. Reconciliation performs destructive and
			// post-await writes, so it must never fall back to owns-then-effect.
			if (lease.commitIfOwned && (await lease.owns(session))) {
				return lease.commitIfOwned(session, effect);
			}
			if (await isOwnerlessPersistedSession(session)) return commitOwnerlessThroughWriterFence(session, effect);
			// An owned row whose lease cannot fence the write, or a row this gate cannot classify at
			// all, fails closed rather than committing unfenced.
			return { kind: "skipped-not-owned" };
		}
	};
}
export async function recoverAdminTerminalSession(
	context: { session: FileUploadSession; evidence: ReconciliationEvidence },
	policy: ReconciliationTerminalPolicy<FileUploadSession, ResumeOutcome> = createPersistedAdminTerminalRecoveryPolicy()
): Promise<ResumeOutcome> {
	return policy.decide(context);
}
